import React from 'react';
import { Box, Typography, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ModalUI } from '../components/UI/Modal/Modal';
import ButtonUI from '../components/UI/Button/Button';
import { getResponsiveSize } from '../utils';

interface PermissionReminderProps {
  open: boolean;
  onClose: () => void;
  onRetry?: () => void;
  imageSrc?: string;
  message?: string;
  buttonLabel?: string;
  closeLabel?: string;
  children?: React.ReactNode;
  pwid?: string;
}

const PermissionReminder: React.FC<PermissionReminderProps> = ({
  open,
  onClose,
  onRetry,
  imageSrc,
  message = 'To proceed, please click “Allow” in your browser to give access to camera and microphone.',
  buttonLabel = 'Retry',
  closeLabel = 'Stop Call',
  children,
  pwid,
}) => {
  const { t } = useTranslation();

  const handleModalClose = (_event: {}, _reason: 'backdropClick' | 'escapeKeyDown') => {
    onClose();
  };

  if (!open) {
    return null;
  }

  return (
    <ModalUI
      open={open}
      onCloseHandler={handleModalClose}
      disableAutoFocus
      wrapperSx={{
        outline: 'none',
        '&:focus': {
          outline: 'none',
        },
        '&:focus-visible': {
          outline: 'none',
        },
      }}
    >
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          p: 4,
          maxWidth: 400,
          textAlign: 'center',
        }}
        data-testid={pwid}
      >
        {imageSrc ? (
          <img src={imageSrc} alt="permission instruction" style={{ width: '100%', marginBottom: 24 }} />
        ) : null}
        {children ? (
          <div style={{ marginBottom: 16 }}>{children}</div>
        ) : (
          <Typography variant="body1" gutterBottom>
            {t(message)}
          </Typography>
        )}

        <Stack direction="column" spacing={1} mt={2} alignItems="center" sx={{ width: '100%' }}>
          <Box sx={{ width: getResponsiveSize(120) }}>
            {onRetry ? (
              <>
                <ButtonUI
                  onClickHandler={() => onRetry()}
                  text={t(buttonLabel)}
                  variant="contained"
                  type="modalPrimary"
                  fullWidth
                  dataTestId={pwid ? `${pwid}-primary` : undefined}
                />
                <Box sx={{ mt: 1 }}>
                  <ButtonUI
                    onClickHandler={() => onClose()}
                    text={t(closeLabel)}
                    variant="outlined"
                    type="modalSecondary"
                    fullWidth
                    dataTestId={pwid ? `${pwid}-secondary` : undefined}
                  />
                </Box>
              </>
            ) : (
              <ButtonUI
                onClickHandler={() => onClose()}
                text={t(buttonLabel)}
                variant="contained"
                type="modalPrimary"
                fullWidth
                dataTestId={pwid ? `${pwid}-primary` : undefined}
              />
            )}
          </Box>
        </Stack>
      </Box>
    </ModalUI>
  );
};

export default PermissionReminder;
